import React, { useState, useContext, useEffect, useCallback } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Row,
    Col,
    Alert,
    Card,
    CardBody,
    Badge,
} from 'reactstrap';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import InputMask from 'react-input-mask';
import PaginationMaterial from '../PaginationMaterial/PaginationMaterial';
import { CurrencyInput } from '../Form/CurrencyInput';
import { AuthContext } from '../../context/AuthContext';
import { AlunoContext } from '../../context/AlunoContext';
import { ProdutoContext } from '../../context/ProdutoContext';
import { FormaPagamentoContext } from '../../context/FormaPagamentoContext';
import { TerceiroContext } from '../../context/TerceiroContext';
import { useToast } from '../../context/ToastContext';
import api from '../../api';

interface CreateVendaModalProps {
    isOpen: boolean;
    toggle: () => void;
    onSuccess: () => void;
}

interface ClienteDTO {
    id: number;
    name: string;
    cpf: string | null;
    email: string | null;
    telefone: string | null;
    celular: string | null;
    endereco: string | null;
    numero: string | null;
    bairro: string | null;
    cidade: string | null;
    estado: string | null;
    cep: string | null;
}

interface FormaPagamentoValue {
    formaPagamentoId: number;
    formaPagamentoName: string;
    value: number;
}

export const CreateVendaModal: React.FC<CreateVendaModalProps> = ({ isOpen, toggle, onSuccess }) => {
    const showToast = useToast();
    const { objUserTk, handleLogout } = useContext(AuthContext);
    const { getAlunoPaged } = useContext(AlunoContext);
    const { handleGetProdutoByEmpresaId, listProdutoByEmpresa } = useContext(ProdutoContext);
    const { handleGetFormaPagamentoByEmpresaId, listFormaPagamentoByEmpresa } = useContext(FormaPagamentoContext);
    const { handleGetVendedoresByEmpresa, listVendedores } = useContext(TerceiroContext);

    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Step 1: Aluno
    const [selectedAluno, setSelectedAluno] = useState<any>(null);
    const [alunosList, setAlunosList] = useState<any[]>([]);
    const [alunosPageCount, setAlunosPageCount] = useState(0);
    const [currentAlunoPage, setCurrentAlunoPage] = useState(1);
    const [searchAlunoNome, setSearchAlunoNome] = useState('');
    const [searchAlunoCodigo, setSearchAlunoCodigo] = useState('');
    const [loadingAlunos, setLoadingAlunos] = useState(false);

    // Step 2: Cliente
    const [selectedCliente, setSelectedCliente] = useState<any>(null);
    const [showCreateCliente, setShowCreateCliente] = useState(true);
    const [loadingCep, setLoadingCep] = useState(false);
    const [clientesList, setClientesList] = useState<ClienteDTO[]>([]);
    const [loadingClientes, setLoadingClientes] = useState(false);
    const [editingClienteId, setEditingClienteId] = useState<number | null>(null);
    const [novoCliente, setNovoCliente] = useState({
        name: '',
        dataNascimento: '',
        cpf: '',
        rg: '',
        orgaoEmissor: '',
        email: '',
        telefone: '',
        celular: '',
        endereco: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: '',
    });

    // Step 3: Produto
    const [selectedProdutos, setSelectedProdutos] = useState<any[]>([]);
    const [produtosList, setProdutosList] = useState<any[]>([]);
    const [produtosPageCount, setProdutosPageCount] = useState(0);
    const [currentProdutoPage, setCurrentProdutoPage] = useState(1);
    const [loadingProdutos, setLoadingProdutos] = useState(false);

    // Step 4: Vendedor
    const [selectedVendedor, setSelectedVendedor] = useState<any>(null);
    const [vendedoresList, setVendedoresList] = useState<any[]>([]);
    const [vendedoresPageCount, setVendedoresPageCount] = useState(0);
    const [currentVendedorPage, setCurrentVendedorPage] = useState(1);
    const [loadingVendedores, setLoadingVendedores] = useState(false);

    // Step 5: Formas de Pagamento
    const [formasPagamento, setFormasPagamento] = useState<FormaPagamentoValue[]>([
        { formaPagamentoId: 0, formaPagamentoName: '', value: 0 }
    ]);

    useEffect(() => {
        if (isOpen && objUserTk.EmpresaId) {
            handleGetFormaPagamentoByEmpresaId(objUserTk.EmpresaId);
            loadAlunos(1, '', '');
            loadProdutos(1);
            loadVendedores(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const loadAlunos = async (page: number, nome: string, codigo: string) => {
        setLoadingAlunos(true);
        try {
            const response = await api.get('/Aluno/getAlunoPaged', {
                params: {
                    pageNumber: page,
                    pageSize: 12,
                    Name: nome || undefined,
                    ReferenceCode: codigo ? Number(codigo) : undefined,
                    ContratoId: 0,
                    EmpresaId: objUserTk.EmpresaId,
                },
            });
            if (response.data?.result) {
                setAlunosList(response.data.result);
                setAlunosPageCount(response.data.pageCount || 0);
                setCurrentAlunoPage(page);
            }
        } catch (error: any) {
            if (error.response?.status === 401) {
                handleLogout();
                showToast('error', 'Sessão expirada. Faça login novamente.');
            } else {
                console.error('Erro ao carregar alunos:', error);
            }
        } finally {
            setLoadingAlunos(false);
        }
    };

    // Debounce para busca por nome
    useEffect(() => {
        if (currentStep === 1 && isOpen) {
            const timeoutId = setTimeout(() => {
                loadAlunos(1, searchAlunoNome, searchAlunoCodigo);
            }, 300);
            return () => clearTimeout(timeoutId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchAlunoNome]);

    // Debounce para busca por código
    useEffect(() => {
        if (currentStep === 1 && isOpen) {
            const timeoutId = setTimeout(() => {
                loadAlunos(1, searchAlunoNome, searchAlunoCodigo);
            }, 300);
            return () => clearTimeout(timeoutId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchAlunoCodigo]);

    const handleSelectAluno = (aluno: any) => {
        // Se está selecionando um aluno diferente, limpa os dados do cliente
        if (selectedAluno?.id !== aluno.id) {
            setSelectedCliente(null);
            setClientesList([]);
            setShowCreateCliente(true);
            setNovoCliente({
                name: '',
                dataNascimento: '',
                cpf: '',
                rg: '',
                orgaoEmissor: '',
                email: '',
                telefone: '',
                celular: '',
                endereco: '',
                numero: '',
                complemento: '',
                bairro: '',
                cidade: '',
                estado: '',
                cep: '',
            });
        }
        setSelectedAluno(aluno);
    };

    const handleRemoveAluno = () => {
        setSelectedAluno(null);
        // Também limpa os dados do cliente quando remove o aluno
        setSelectedCliente(null);
        setClientesList([]);
        setShowCreateCliente(true);
        setNovoCliente({
            name: '',
            dataNascimento: '',
            cpf: '',
            rg: '',
            orgaoEmissor: '',
            email: '',
            telefone: '',
            celular: '',
            endereco: '',
            numero: '',
            complemento: '',
            bairro: '',
            cidade: '',
            estado: '',
            cep: '',
        });
    };

    const handleAlunoPageChange = (_: any, page: number) => {
        loadAlunos(page, searchAlunoNome, searchAlunoCodigo);
    };

    const loadProdutos = async (page: number) => {
        setLoadingProdutos(true);
        try {
            const response = await api.get('/Produto/getByEmpresaId/' + objUserTk.EmpresaId, {
                params: {
                    pageNumber: page,
                    pageSize: 12,
                },
            });
            if (response.data?.result) {
                setProdutosList(response.data.result);
                setProdutosPageCount(response.data.pageCount || 0);
                setCurrentProdutoPage(page);
            } else if (response.data) {
                // Se a API retorna array direto (sem paginação)
                const allProdutos = Array.isArray(response.data) ? response.data : [];
                const pageSize = 12;
                const startIndex = (page - 1) * pageSize;
                const endIndex = startIndex + pageSize;
                setProdutosList(allProdutos.slice(startIndex, endIndex));
                setProdutosPageCount(Math.ceil(allProdutos.length / pageSize));
                setCurrentProdutoPage(page);
            }
        } catch (error: any) {
            if (error.response?.status === 401) {
                handleLogout();
                showToast('error', 'Sessão expirada. Faça login novamente.');
            } else {
                console.error('Erro ao carregar produtos:', error);
            }
        } finally {
            setLoadingProdutos(false);
        }
    };

    const handleProdutoPageChange = (_: any, page: number) => {
        loadProdutos(page);
    };

    const loadVendedores = async (page: number) => {
        setLoadingVendedores(true);
        try {
            const response = await api.get(`/Terceiro/getTerceirosByTipoForEmpresa?empresaId=${objUserTk.EmpresaId}&tipo=VENDEDOR`);
            
            if (response.data) {
                const allVendedores = Array.isArray(response.data) ? response.data : [];
                const pageSize = 12;
                const startIndex = (page - 1) * pageSize;
                const endIndex = startIndex + pageSize;
                setVendedoresList(allVendedores.slice(startIndex, endIndex));
                setVendedoresPageCount(Math.ceil(allVendedores.length / pageSize));
                setCurrentVendedorPage(page);
            }
        } catch (error: any) {
            if (error.response?.status === 401) {
                handleLogout();
                showToast('error', 'Sessão expirada. Faça login novamente.');
            } else {
                console.error('Erro ao carregar vendedores:', error);
            }
        } finally {
            setLoadingVendedores(false);
        }
    };

    const handleVendedorPageChange = (_: any, page: number) => {
        loadVendedores(page);
    };

    const loadClientes = async () => {
        if (!selectedAluno) return;

        setLoadingClientes(true);
        try {
            const response = await api.get('/Cliente/getAllClientePaged', {
                params: {
                    EmpresaId: objUserTk.EmpresaId,
                    AlunoId: selectedAluno.id,
                    PageNumber: 1,
                    PageSize: 100,
                },
            });

            if (response.data?.result) {
                setClientesList(response.data.result);
                // Se não houver clientes, já mostra o formulário de cadastro
                if (response.data.result.length === 0) {
                    setShowCreateCliente(true);
                } else {
                    setShowCreateCliente(false);
                }
            }
        } catch (error: any) {
            if (error.response?.status === 401) {
                handleLogout();
                showToast('error', 'Sessão expirada. Faça login novamente.');
            } else {
                console.error('Erro ao carregar clientes:', error);
            }
        } finally {
            setLoadingClientes(false);
        }
    };

    // Carregar clientes quando entrar no step 2
    useEffect(() => {
        if (currentStep === 2 && isOpen && selectedAluno) {
            loadClientes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, isOpen, selectedAluno]);

    const handleSelectCliente = (cliente: ClienteDTO) => {
        setSelectedCliente({
            value: cliente.id,
            label: cliente.name,
            data: cliente,
        });
    };

    const handleEditCliente = (cliente: ClienteDTO) => {
        setEditingClienteId(cliente.id);
        setNovoCliente({
            name: cliente.name || '',
            dataNascimento: '',
            cpf: cliente.cpf || '',
            rg: '',
            orgaoEmissor: '',
            email: cliente.email || '',
            telefone: cliente.telefone || '',
            celular: cliente.celular || '',
            endereco: cliente.endereco || '',
            numero: cliente.numero || '',
            complemento: '',
            bairro: cliente.bairro || '',
            cidade: cliente.cidade || '',
            estado: cliente.estado || '',
            cep: cliente.cep || '',
        });
        setShowCreateCliente(true);
    };

    const loadClientesOptions = async (inputValue: string) => {
        if (!selectedAluno) return [];

        try {
            const response = await api.get('/Cliente/getAllClientePaged', {
                params: {
                    EmpresaId: objUserTk.EmpresaId,
                    AlunoId: selectedAluno.id,
                    PageNumber: 1,
                    PageSize: 50,
                },
            });

            if (response.data?.result) {
                let clientes = response.data.result;
                if (inputValue) {
                    clientes = clientes.filter((c: ClienteDTO) =>
                        c.name?.toLowerCase().includes(inputValue.toLowerCase()) ||
                        c.cpf?.includes(inputValue) ||
                        c.email?.toLowerCase().includes(inputValue.toLowerCase())
                    );
                }
                return clientes.map((cliente: ClienteDTO) => ({
                    value: cliente.id,
                    label: `${cliente.name}${cliente.cpf ? ' - CPF: ' + cliente.cpf : ''}${cliente.email ? ' - ' + cliente.email : ''}`,
                    data: cliente,
                }));
            }
            return [];
        } catch (error: any) {
            if (error.response?.status === 401) {
                handleLogout();
                showToast('error', 'Sessão expirada. Faça login novamente.');
            } else {
                console.error('Erro ao carregar clientes:', error);
            }
            return [];
        }
    };

    const buscarCep = async (cep: string) => {
        const cepLimpo = cep.replace(/\D/g, '');
        if (cepLimpo.length !== 8) return;

        setLoadingCep(true);
        try {
            const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
            const data = await response.json();

            if (!data.erro) {
                setNovoCliente(prev => ({
                    ...prev,
                    endereco: data.logradouro || '',
                    bairro: data.bairro || '',
                    cidade: data.localidade || '',
                    estado: data.uf || '',
                }));
            } else {
                showToast('warning', 'CEP não encontrado.');
            }
        } catch (error) {
            console.error('Erro ao buscar CEP:', error);
            showToast('error', 'Erro ao buscar CEP.');
        } finally {
            setLoadingCep(false);
        }
    };

    const preencherComDadosAluno = () => {
        if (!selectedAluno) return;

        setNovoCliente({
            name: selectedAluno.name || '',
            dataNascimento: '',
            cpf: selectedAluno.cpf || '',
            rg: '',
            orgaoEmissor: '',
            email: selectedAluno.email || '',
            telefone: selectedAluno.landline || '',
            celular: selectedAluno.telephone_01 || '',
            endereco: selectedAluno.address || '',
            numero: selectedAluno.addressNumber || '',
            complemento: '',
            bairro: selectedAluno.neighborhood || '',
            cidade: selectedAluno.city || '',
            estado: selectedAluno.state || '',
            cep: selectedAluno.zipCode || '',
        });
        showToast('success', 'Dados do aluno preenchidos com sucesso!');
    };

    const handleCreateCliente = async () => {
        if (!novoCliente.name.trim()) {
            showToast('error', 'Nome do cliente é obrigatório.');
            return;
        }

        if (!selectedAluno) {
            showToast('error', 'Selecione um aluno antes de cadastrar o cliente.');
            return;
        }

        try {
            const payload = {
                ...novoCliente,
                dataNascimento: novoCliente.dataNascimento || null,
                empresaId: objUserTk.EmpresaId,
                alunoId: selectedAluno.id,
            };

            let response;
            if (editingClienteId) {
                // Modo de edição - atualizar cliente existente
                response = await api.put(`/Cliente/update/${editingClienteId}`, {
                    id: editingClienteId,
                    ...payload,
                });
                showToast('success', 'Cliente atualizado com sucesso!');
            } else {
                // Modo de criação - criar novo cliente
                response = await api.post('/Cliente/create', payload);
                showToast('success', 'Cliente cadastrado com sucesso!');
            }

            if (response.data?.id || editingClienteId) {
                const clienteId = response.data?.id || editingClienteId;
                setSelectedCliente({
                    value: clienteId,
                    label: novoCliente.name,
                    data: { ...novoCliente, id: clienteId },
                });
                setShowCreateCliente(false);
                setEditingClienteId(null);
                setNovoCliente({
                    name: '',
                    dataNascimento: '',
                    cpf: '',
                    rg: '',
                    orgaoEmissor: '',
                    email: '',
                    telefone: '',
                    celular: '',
                    endereco: '',
                    numero: '',
                    complemento: '',
                    bairro: '',
                    cidade: '',
                    estado: '',
                    cep: '',
                });
                // Recarrega a lista de clientes
                loadClientes();
            }
        } catch (error: any) {
            if (error.response?.status === 401) {
                handleLogout();
                showToast('error', 'Sessão expirada. Faça login novamente.');
            } else {
                showToast('error', error.response?.data?.message || `Erro ao ${editingClienteId ? 'atualizar' : 'cadastrar'} cliente.`);
            }
        }
    };

    const handleAddFormaPagamento = () => {
        setFormasPagamento([...formasPagamento, { formaPagamentoId: 0, formaPagamentoName: '', value: 0 }]);
    };

    const handleRemoveFormaPagamento = (index: number) => {
        if (formasPagamento.length > 1) {
            const newFormas = formasPagamento.filter((_, i) => i !== index);
            setFormasPagamento(newFormas);
        }
    };

    const handleFormaPagamentoChange = (index: number, field: 'formaPagamentoId' | 'value', value: any) => {
        const newFormas = [...formasPagamento];
        if (field === 'formaPagamentoId') {
            const forma = listFormaPagamentoByEmpresa.find(f => f.id === value);
            newFormas[index].formaPagamentoId = value;
            newFormas[index].formaPagamentoName = forma?.name || '';
        } else {
            newFormas[index][field] = value;
        }
        setFormasPagamento(newFormas);
    };

    const getTotalAlocado = () => {
        return formasPagamento.reduce((sum, fp) => sum + (fp.value || 0), 0);
    };

    const getValorRestante = () => {
        const produtoValue = selectedProdutos.reduce((sum, p) => sum + (p.data?.value || 0), 0);
        return produtoValue - getTotalAlocado();
    };

    const isFormasPagamentoValidas = () => {
        const produtoValue = selectedProdutos.reduce((sum, p) => sum + (p.data?.value || 0), 0);
        const totalAlocado = getTotalAlocado();
        return Math.abs(produtoValue - totalAlocado) < 0.01; // Tolerância de 1 centavo
    };

    const handleNextStep = () => {
        if (currentStep === 1 && !selectedAluno) {
            showToast('error', 'Selecione um aluno para continuar.');
            return;
        }
        if (currentStep === 2 && !selectedCliente) {
            showToast('error', 'Selecione ou cadastre um cliente para continuar.');
            return;
        }
        if (currentStep === 3 && selectedProdutos.length === 0) {
            showToast('error', 'Selecione ao menos um produto para continuar.');
            return;
        }
        if (currentStep === 4 && !selectedVendedor) {
            showToast('error', 'Selecione um vendedor para continuar.');
            return;
        }
        if (currentStep === 5 && !isFormasPagamentoValidas()) {
            showToast('error', 'A soma das formas de pagamento deve ser igual ao valor do produto.');
            return;
        }

        setCurrentStep(currentStep + 1);
    };

    const handlePreviousStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSubmit = async () => {
        if (!isFormasPagamentoValidas()) {
            showToast('error', 'A soma das formas de pagamento deve ser igual ao valor total dos produtos.');
            return;
        }

        setIsSubmitting(true);
        try {
            const valorTotal = selectedProdutos.reduce((sum, p) => sum + (p.data?.value || 0), 0);
            let vendasCriadas = 0;
            let boletosGerados: any[] = [];

            // Criar uma venda para cada produto
            for (const produto of selectedProdutos) {
                const valorProduto = produto.data.value;
                const proporcao = valorProduto / valorTotal;

                // Distribuir formas de pagamento proporcionalmente
                const formaPagamentoValuesParaProduto = formasPagamento.map(fp => ({
                    formaPagamentoId: fp.formaPagamentoId,
                    value: fp.value * proporcao,
                }));

                const payload = {
                    empresaId: objUserTk.EmpresaId,
                    produtoId: produto.value,
                    clienteId: selectedCliente.value,
                    vendedorId: selectedVendedor.value,
                    formaPagamentoValues: formaPagamentoValuesParaProduto,
                };

                const response = await api.post('/Venda/create', payload);
                vendasCriadas++;

                // Coletar boletos se houver
                if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                    boletosGerados = [...boletosGerados, ...response.data];
                }
            }

            // Mostrar mensagem de sucesso
            const mensagem = selectedProdutos.length > 1 
                ? `${vendasCriadas} vendas criadas com sucesso!`
                : 'Venda criada com sucesso!';
            
            if (boletosGerados.length > 0) {
                showToast('success', `${mensagem} ${boletosGerados.length} boleto(s) gerado(s).`);
                
                // Oferecer download dos boletos
                boletosGerados.forEach((boleto: any, index: number) => {
                    if (boleto.pdf) {
                        const blob = new Blob([Uint8Array.from(atob(boleto.pdf), c => c.charCodeAt(0))], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `boleto_${boleto.nosso_numero || index + 1}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                    }
                });
            } else {
                showToast('success', mensagem);
            }

            handleClose();
            onSuccess();
        } catch (error: any) {
            if (error.response?.status === 401) {
                handleLogout();
                showToast('error', 'Sessão expirada. Faça login novamente.');
            } else if (error.response?.status === 404) {
                showToast('error', error.response.data || 'Registro não encontrado.');
            } else if (error.response?.status === 400) {
                showToast('error', error.response.data || 'Erro ao criar a venda.');
            } else {
                showToast('error', 'Erro inesperado ao criar a venda.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setCurrentStep(1);
        setSelectedAluno(null);
        setAlunosList([]);
        setAlunosPageCount(0);
        setCurrentAlunoPage(1);
        setSearchAlunoNome('');
        setSearchAlunoCodigo('');
        setSelectedCliente(null);
        setClientesList([]);
        setSelectedProdutos([]);
        setSelectedVendedor(null);
        setFormasPagamento([{ formaPagamentoId: 0, formaPagamentoName: '', value: 0 }]);
        setShowCreateCliente(true);
        setNovoCliente({
            name: '',
            dataNascimento: '',
            cpf: '',
            rg: '',
            orgaoEmissor: '',
            email: '',
            telefone: '',
            celular: '',
            endereco: '',
            numero: '',
            complemento: '',
            bairro: '',
            cidade: '',
            estado: '',
            cep: '',
        });
        toggle();
    };

    const renderStepIndicator = () => {
        const steps = [
            { num: 1, title: 'Aluno' },
            { num: 2, title: 'Cliente' },
            { num: 3, title: 'Produto' },
            { num: 4, title: 'Vendedor' },
            { num: 5, title: 'Pagamento' },
            { num: 6, title: 'Confirmar' },
        ];

        return (
            <div className="d-flex justify-content-between mb-2">
                {steps.map((step, index) => (
                    <div key={step.num} className="text-center" style={{ flex: 1 }}>
                        <div
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: currentStep >= step.num ? '#5e72e4' : '#e9ecef',
                                color: currentStep >= step.num ? '#fff' : '#adb5bd',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 4px auto',
                                fontWeight: 'bold',
                            }}
                        >
                            {step.num}
                        </div>
                        <small className={currentStep >= step.num ? 'text-primary' : 'text-muted'}>
                            {step.title}
                        </small>
                        {index < steps.length - 1 && (
                            <hr
                                style={{
                                    position: 'relative',
                                    top: '-22px',
                                    left: '50%',
                                    width: '100%',
                                    margin: 0,
                                    zIndex: -1,
                                    borderTop: `2px ${currentStep > step.num ? 'solid' : 'dashed'} ${currentStep > step.num ? '#5e72e4' : '#e9ecef'}`,
                                }}
                            />
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const renderStep1 = () => (
        <>
            <div className="mb-1">
                <h5 className="text-primary mb-1">
                    <i className="fa fa-user-graduate mr-2"></i>
                    Selecione o Aluno
                </h5>
                <p className="text-muted">Busque e selecione o aluno para esta venda. Use os filtros abaixo para facilitar a busca.</p>
            </div>

            <Row className="mb-3">
                <Col md={6}>
                    <FormGroup>
                        <Label>Buscar por Nome</Label>
                        <Input
                            type="text"
                            placeholder="Digite o nome do aluno..."
                            value={searchAlunoNome}
                            onChange={(e) => setSearchAlunoNome(e.target.value)}
                            className="form-control-alternative"
                        />
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label>Buscar por Código de Referência</Label>
                        <Input
                            type="number"
                            placeholder="Digite o código..."
                            value={searchAlunoCodigo}
                            onChange={(e) => setSearchAlunoCodigo(e.target.value)}
                            className="form-control-alternative"
                        />
                    </FormGroup>
                </Col>
            </Row>

            {loadingAlunos ? (
                <div className="text-center py-5">
                    <i className="fa fa-spinner fa-spin fa-2x"></i>
                    <p className="mt-2">Carregando alunos...</p>
                </div>
            ) : alunosList.length === 0 ? (
                <Alert color="warning">
                    <i className="fa fa-exclamation-triangle mr-2"></i>
                    Nenhum aluno encontrado.
                </Alert>
            ) : (
                <>
                    <Row>
                        {alunosList.map((aluno) => (
                            <Col md={6} lg={4} key={aluno.id} className="mb-3">
                                <Card
                                    className={`h-100 cursor-pointer ${
                                        selectedAluno?.id === aluno.id
                                            ? 'border-success shadow'
                                            : 'border'
                                    }`}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        borderWidth: selectedAluno?.id === aluno.id ? '2px' : '1px',
                                    }}
                                    onClick={() => handleSelectAluno(aluno)}
                                >
                                    <CardBody>
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <h5 className="mb-0">
                                                {selectedAluno?.id === aluno.id && (
                                                    <i className="fa fa-check-circle text-success mr-2"></i>
                                                )}
                                                {aluno.name}
                                            </h5>
                                            {aluno.referenceCode && (
                                                <Badge color="primary">#{aluno.referenceCode}</Badge>
                                            )}
                                        </div>
                                        <p className="text-muted mb-1">
                                            <i className="fa fa-graduation-cap mr-2"></i>
                                            {aluno.cursoName || 'Sem curso'}
                                        </p>
                                        {aluno.email && (
                                            <p className="text-muted mb-0 small">
                                                <i className="fa fa-envelope mr-2"></i>
                                                {aluno.email}
                                            </p>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {alunosPageCount > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                            <PaginationMaterial
                                count={alunosPageCount}
                                onChange={handleAlunoPageChange}
                            />
                        </div>
                    )}
                </>
            )}

            {selectedAluno && (
                <Alert color="success" className="mt-4">
                    <Row className="align-items-center">
                        <Col>
                            <strong><i className="fa fa-check-circle mr-2"></i>Aluno Selecionado:</strong> {selectedAluno.name}<br />
                            <small>
                                Curso: {selectedAluno.cursoName || 'Não informado'} | 
                                Ref: {selectedAluno.referenceCode || 'N/A'}
                            </small>
                        </Col>
                        <Col xs="auto">
                            <Button
                                size="sm"
                                color="danger"
                                onClick={handleRemoveAluno}
                            >
                                <i className="fa fa-times"></i>
                            </Button>
                        </Col>
                    </Row>
                </Alert>
            )}
        </>
    );

    const renderStep2 = () => (
        <>
            <div className="mb-1">
                <h5 className="text-primary mb-1">
                    <i className="fa fa-address-card mr-2"></i>
                    Dados do Cliente
                </h5>
                <p className="text-muted">
                    {selectedAluno ? `Cadastre um novo cliente ou selecione um existente vinculado a ${selectedAluno.name}.` : 'Selecione um aluno primeiro.'}
                </p>
            </div>

            {showCreateCliente ? (
                <Card>
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="mb-0">{editingClienteId ? 'Editar Cliente' : 'Cadastrar Novo Cliente'}</h4>
                            <div>
                                {clientesList.length > 0 && (
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        outline
                                        onClick={() => {
                                            setShowCreateCliente(false);
                                            setEditingClienteId(null);
                                        }}
                                        className="mr-2"
                                    >
                                        <i className="fa fa-search mr-1"></i>
                                        Selecionar Existente ({clientesList.length})
                                    </Button>
                                )}
                                <Button
                                    color="info"
                                    size="sm"
                                    outline
                                    onClick={preencherComDadosAluno}
                                >
                                    <i className="fa fa-copy mr-1"></i>
                                    Usar Dados do Aluno
                                </Button>
                            </div>
                        </div>

                        <h6 className="text-primary mb-3">
                            <i className="fa fa-user mr-2"></i>
                            Dados Pessoais
                        </h6>
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label>Nome <span className="text-danger">*</span></Label>
                                    <Input
                                        type="text"
                                        value={novoCliente.name}
                                        onChange={(e) => setNovoCliente({ ...novoCliente, name: e.target.value })}
                                        placeholder="Nome completo"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Data de Nascimento</Label>
                                    <Input
                                        type="date"
                                        value={novoCliente.dataNascimento}
                                        onChange={(e) => setNovoCliente({ ...novoCliente, dataNascimento: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>CPF</Label>
                                    <InputMask
                                        mask="999.999.999-99"
                                        value={novoCliente.cpf}
                                        onChange={(e: any) => setNovoCliente({ ...novoCliente, cpf: e.target.value })}
                                    >
                                        {(inputProps: any) => <Input {...inputProps} type="text" placeholder="000.000.000-00" />}
                                    </InputMask>
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>RG</Label>
                                    <Input
                                        type="text"
                                        value={novoCliente.rg}
                                        onChange={(e) => setNovoCliente({ ...novoCliente, rg: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Órgão Emissor</Label>
                                    <Input
                                        type="text"
                                        value={novoCliente.orgaoEmissor}
                                        onChange={(e) => setNovoCliente({ ...novoCliente, orgaoEmissor: e.target.value })}
                                        placeholder="SSP, DETRAN, etc"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        value={novoCliente.email}
                                        onChange={(e) => setNovoCliente({ ...novoCliente, email: e.target.value })}
                                        placeholder="email@exemplo.com"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Telefone</Label>
                                    <InputMask
                                        mask="(99) 9999-9999"
                                        value={novoCliente.telefone}
                                        onChange={(e: any) => setNovoCliente({ ...novoCliente, telefone: e.target.value })}
                                    >
                                        {(inputProps: any) => <Input {...inputProps} type="text" placeholder="(00) 0000-0000" />}
                                    </InputMask>
                                </FormGroup>
                            </Col>
                        </Row>

                        <hr />
                        
                        <h6 className="text-primary mb-3 mt-3">
                            <i className="fa fa-map-marker mr-2"></i>
                            Endereço
                        </h6>
                        <Row>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>CEP <span className="text-danger">*</span></Label>
                                    <InputMask
                                        mask="99999-999"
                                        value={novoCliente.cep}
                                        onChange={(e: any) => setNovoCliente({ ...novoCliente, cep: e.target.value })}
                                        onBlur={(e: any) => buscarCep(e.target.value)}
                                        disabled={loadingCep}
                                    >
                                        {(inputProps: any) => <Input {...inputProps} type="text" placeholder="00000-000" />}
                                    </InputMask>
                                    {loadingCep && <small className="text-info">Buscando...</small>}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Logradouro</Label>
                                    <Input
                                        type="text"
                                        value={novoCliente.endereco}
                                        onChange={(e) => setNovoCliente({ ...novoCliente, endereco: e.target.value })}
                                        placeholder="Rua, Avenida, etc"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Número</Label>
                                    <Input
                                        type="text"
                                        value={novoCliente.numero}
                                        onChange={(e) => setNovoCliente({ ...novoCliente, numero: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Complemento</Label>
                                    <Input
                                        type="text"
                                        value={novoCliente.complemento}
                                        onChange={(e) => setNovoCliente({ ...novoCliente, complemento: e.target.value })}
                                        placeholder="Apto, Bloco, etc"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Bairro</Label>
                                    <Input
                                        type="text"
                                        value={novoCliente.bairro}
                                        onChange={(e) => setNovoCliente({ ...novoCliente, bairro: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Cidade</Label>
                                    <Input
                                        type="text"
                                        value={novoCliente.cidade}
                                        onChange={(e) => setNovoCliente({ ...novoCliente, cidade: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Estado</Label>
                                    <Input
                                        type="text"
                                        value={novoCliente.estado}
                                        onChange={(e) => setNovoCliente({ ...novoCliente, estado: e.target.value })}
                                        maxLength={2}
                                        placeholder="UF"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={12}>
                                <FormGroup>
                                    <Label>Celular</Label>
                                    <InputMask
                                        mask="(99) 99999-9999"
                                        value={novoCliente.celular}
                                        onChange={(e: any) => setNovoCliente({ ...novoCliente, celular: e.target.value })}
                                    >
                                        {(inputProps: any) => <Input {...inputProps} type="text" placeholder="(00) 00000-0000" />}
                                    </InputMask>
                                </FormGroup>
                            </Col>
                        </Row>
                        <div className="d-flex justify-content-between">
                            <Button
                                style={{ color: "#FFF", backgroundColor: "#d9534f" }}
                                onClick={() => {
                                    setShowCreateCliente(false);
                                    setEditingClienteId(null);
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                style={{ color: "#FFF", backgroundColor: "#585858" }}
                                onClick={handleCreateCliente}
                            >
                                {editingClienteId ? 'Atualizar Cliente' : 'Salvar Cliente'}
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            ) : (
                <>
                    {loadingClientes ? (
                        <div className="text-center py-5">
                            <i className="fa fa-spinner fa-spin fa-2x"></i>
                            <p className="mt-2">Carregando clientes...</p>
                        </div>
                    ) : clientesList.length === 0 ? (
                        <Alert color="info">
                            <i className="fa fa-info-circle mr-2"></i>
                            Nenhum cliente cadastrado para este aluno. Cadastre um novo cliente.
                        </Alert>
                    ) : (
                        <>
                            <h6 className="mb-3">
                                <i className="fa fa-users mr-2"></i>
                                Selecione um Cliente ({clientesList.length} encontrado{clientesList.length > 1 ? 's' : ''})
                            </h6>
                            <Row>
                                {clientesList.map((cliente) => (
                                    <Col md={6} key={cliente.id} className="mb-3">
                                        <Card
                                            className={`h-100 ${
                                                selectedCliente?.value === cliente.id
                                                    ? 'border-success shadow'
                                                    : 'border'
                                            }`}
                                            style={{
                                                transition: 'all 0.2s',
                                                borderWidth: selectedCliente?.value === cliente.id ? '2px' : '1px',
                                            }}
                                        >
                                            <CardBody>
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <h5 className="mb-0" style={{ cursor: 'pointer' }} onClick={() => handleSelectCliente(cliente)}>
                                                        {selectedCliente?.value === cliente.id && (
                                                            <i className="fa fa-check-circle text-success mr-2"></i>
                                                        )}
                                                        {cliente.name}
                                                    </h5>
                                                    <Button
                                                        color="info"
                                                        size="sm"
                                                        outline
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditCliente(cliente);
                                                        }}
                                                        title="Editar cliente"
                                                    >
                                                        <i className="fa fa-edit"></i>
                                                    </Button>
                                                </div>
                                                <div style={{ cursor: 'pointer' }} onClick={() => handleSelectCliente(cliente)}>
                                                    {cliente.cpf && (
                                                        <p className="text-muted mb-1">
                                                            <i className="fa fa-id-card mr-2"></i>
                                                            CPF: {cliente.cpf}
                                                        </p>
                                                    )}
                                                    {cliente.email && (
                                                        <p className="text-muted mb-1 small">
                                                            <i className="fa fa-envelope mr-2"></i>
                                                            {cliente.email}
                                                        </p>
                                                    )}
                                                    {(cliente.telefone || cliente.celular) && (
                                                        <p className="text-muted mb-0 small">
                                                            <i className="fa fa-phone mr-2"></i>
                                                            {cliente.celular || cliente.telefone}
                                                        </p>
                                                    )}
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                            <div className="text-center my-3">
                                <span className="text-muted">— OU —</span>
                            </div>
                            <Button
                                style={{ color: "#FFF", backgroundColor: "#585858" }}
                                block
                                onClick={() => setShowCreateCliente(true)}
                            >
                                <i className="fa fa-plus mr-2"></i>
                                Cadastrar Novo Cliente
                            </Button>
                        </>
                    )}
                </>
            )}

            {selectedCliente && (
                <Alert color="success" className="mt-4">
                    <Row className="align-items-center">
                        <Col>
                            <strong><i className="fa fa-check-circle mr-2"></i>Cliente Selecionado:</strong> {selectedCliente.label || selectedCliente.data?.name}<br />
                            {selectedCliente.data?.cpf && (
                                <small>CPF: {selectedCliente.data.cpf}</small>
                            )}
                        </Col>
                        <Col xs="auto">
                            <Button
                                size="sm"
                                color="danger"
                                onClick={() => setSelectedCliente(null)}
                            >
                                <i className="fa fa-times"></i>
                            </Button>
                        </Col>
                    </Row>
                </Alert>
            )}
        </>
    );

    const renderStep3 = () => (
        <>
            <div className="mb-1">
                <h5 className="text-primary mb-1">
                    <i className="fa fa-box mr-2"></i>
                    Selecione o Produto
                </h5>
                <p className="text-muted">Escolha um ou mais produtos (álbum ou serviço). Será criada uma venda para cada produto selecionado.</p>
            </div>

            {loadingProdutos ? (
                <div className="text-center py-5">
                    <i className="fa fa-spinner fa-spin fa-2x"></i>
                    <p className="mt-2">Carregando produtos...</p>
                </div>
            ) : produtosList.length === 0 ? (
                <Alert color="warning">
                    <i className="fa fa-exclamation-triangle mr-2"></i>
                    Nenhum produto cadastrado.
                </Alert>
            ) : (
                <>
                    <Row>
                        {produtosList.map((produto) => {
                            const isSelected = selectedProdutos.some(p => p.value === produto.id);
                            return (
                                <Col md={6} key={produto.id} className="mb-3">
                                    <Card
                                        className={`h-100 cursor-pointer ${
                                            isSelected
                                                ? 'border-success shadow'
                                                : 'border'
                                        }`}
                                        style={{
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            borderWidth: isSelected ? '2px' : '1px',
                                        }}
                                        onClick={() => {
                                            const produtoObj = { value: produto.id, label: produto.name, data: produto };
                                            if (isSelected) {
                                                setSelectedProdutos(selectedProdutos.filter(p => p.value !== produto.id));
                                            } else {
                                                setSelectedProdutos([...selectedProdutos, produtoObj]);
                                            }
                                        }}
                                    >
                                        <CardBody>
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h5 className="mb-0">
                                                    {isSelected && (
                                                        <i className="fa fa-check-circle text-success mr-2"></i>
                                                    )}
                                                    {produto.name}
                                                </h5>
                                                <Badge color="primary" pill>
                                                    R$ {(produto.value || 0).toFixed(2).replace('.', ',')}
                                                </Badge>
                                            </div>
                                            {produto.description && (
                                                <p className="text-muted mb-0 small">
                                                    <i className="fa fa-info-circle mr-2"></i>
                                                    {produto.description}
                                                </p>
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>

                    {produtosPageCount > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                            <PaginationMaterial
                                count={produtosPageCount}
                                onChange={handleProdutoPageChange}
                            />
                        </div>
                    )}
                </>
            )}

            {selectedProdutos.length > 0 && (
                <Alert color="success" className="mt-3">
                    <Row className="align-items-center">
                        <Col>
                            <strong><i className="fa fa-check-circle mr-2"></i>
                                {selectedProdutos.length} Produto{selectedProdutos.length > 1 ? 's' : ''} Selecionado{selectedProdutos.length > 1 ? 's' : ''}:
                            </strong>
                            <div className="mt-2">
                                {selectedProdutos.map((prod, idx) => (
                                    <Badge key={prod.value} color="success" className="mr-2 mb-1" style={{ fontSize: '0.9em', padding: '0.5em 0.8em' }}>
                                        {prod.data.name} - R$ {prod.data.value.toFixed(2).replace('.', ',')}
                                        <i 
                                            className="fa fa-times ml-2" 
                                            style={{ cursor: 'pointer' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedProdutos(selectedProdutos.filter(p => p.value !== prod.value));
                                            }}
                                        ></i>
                                    </Badge>
                                ))}
                            </div>
                        </Col>
                        <Col xs="auto">
                            <Button
                                size="sm"
                                color="danger"
                                onClick={() => setSelectedProdutos([])}
                            >
                                Limpar Todos
                            </Button>
                        </Col>
                    </Row>
                </Alert>
            )}
        </>
    );

    const renderStep4 = () => (
        <>
            <div className="mb-1">
                <h5 className="text-primary mb-1">
                    <i className="fa fa-user-tie mr-2"></i>
                    Selecione o Vendedor
                </h5>
                <p className="text-muted">Escolha o vendedor responsável por esta venda.</p>
            </div>

            {loadingVendedores ? (
                <div className="text-center py-5">
                    <i className="fa fa-spinner fa-spin fa-2x"></i>
                    <p className="mt-2">Carregando vendedores...</p>
                </div>
            ) : vendedoresList.length === 0 ? (
                <Alert color="warning">
                    <i className="fa fa-exclamation-triangle mr-2"></i>
                    Nenhum vendedor cadastrado.
                </Alert>
            ) : (
                <>
                    <Row>
                        {vendedoresList.map((vendedor) => (
                            <Col md={6} key={vendedor.id} className="mb-3">
                                <Card
                                    className={`h-100 cursor-pointer ${
                                        selectedVendedor?.value === vendedor.id
                                            ? 'border-success shadow'
                                            : 'border'
                                    }`}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        borderWidth: selectedVendedor?.value === vendedor.id ? '2px' : '1px',
                                    }}
                                    onClick={() => setSelectedVendedor({ value: vendedor.id, label: vendedor.name, data: vendedor })}
                                >
                                    <CardBody>
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <h5 className="mb-0">
                                                {selectedVendedor?.value === vendedor.id && (
                                                    <i className="fa fa-check-circle text-success mr-2"></i>
                                                )}
                                                {vendedor.name}
                                            </h5>
                                        </div>
                                        {vendedor.email && (
                                            <p className="text-muted mb-1 small">
                                                <i className="fa fa-envelope mr-2"></i>
                                                {vendedor.email}
                                            </p>
                                        )}
                                        {(vendedor.telefone || vendedor.celular) && (
                                            <p className="text-muted mb-0 small">
                                                <i className="fa fa-phone mr-2"></i>
                                                {vendedor.celular || vendedor.telefone}
                                            </p>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {vendedoresPageCount > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                            <PaginationMaterial
                                count={vendedoresPageCount}
                                onChange={handleVendedorPageChange}
                            />
                        </div>
                    )}
                </>
            )}

            {selectedVendedor && (
                <Alert color="success" className="mt-3">
                    <Row className="align-items-center">
                        <Col>
                            <strong><i className="fa fa-check-circle mr-2"></i>Vendedor Selecionado:</strong> {selectedVendedor.data.name}
                        </Col>
                        <Col xs="auto">
                            <Button
                                size="sm"
                                color="danger"
                                onClick={() => setSelectedVendedor(null)}
                            >
                                <i className="fa fa-times"></i>
                            </Button>
                        </Col>
                    </Row>
                </Alert>
            )}
        </>
    );

    const renderStep5 = () => {
        const produtoValue = selectedProdutos.reduce((sum, p) => sum + (p.data?.value || 0), 0);
        const totalAlocado = getTotalAlocado();
        const restante = getValorRestante();

        return (
            <>
                <div className="mb-1">
                    <h5 className="text-primary mb-1">
                        <i className="fa fa-credit-card mr-2"></i>
                        Formas de Pagamento
                    </h5>
                    <p className="text-muted">
                        Configure as formas de pagamento. A soma total deve ser igual ao valor {selectedProdutos.length > 1 ? 'total dos produtos' : 'do produto'} (R$ {produtoValue.toFixed(2).replace('.', ',')}).
                    </p>
                </div>
                <Card className="mb-4" style={{ backgroundColor: Math.abs(restante) < 0.01 ? '#d4edda' : '#fff3cd', border: 'none' }}>
                    <CardBody>
                        <Row className="align-items-center">
                            <Col md={4} className="text-center">
                                <div>
                                    <small className="text-muted d-block">Valor do Produto</small>
                                    <h4 className="mb-0">R$ {produtoValue.toFixed(2).replace('.', ',')}</h4>
                                </div>
                            </Col>
                            <Col md={4} className="text-center border-left border-right">
                                <div>
                                    <small className="text-muted d-block">Total Alocado</small>
                                    <h4 className="mb-0">R$ {totalAlocado.toFixed(2).replace('.', ',')}</h4>
                                </div>
                            </Col>
                            <Col md={4} className="text-center">
                                <div>
                                    <small className="text-muted d-block">Restante</small>
                                    <h4 className={`mb-0 ${restante < 0 ? 'text-danger' : restante > 0 ? 'text-warning' : 'text-success'}`}>
                                        R$ {Math.abs(restante).toFixed(2).replace('.', ',')}
                                    </h4>
                                    {restante < 0 && <Badge color="danger" className="mt-1">EXCEDENTE</Badge>}
                                    {restante > 0 && <Badge color="warning" className="mt-1">FALTA ALOCAR</Badge>}
                                    {Math.abs(restante) < 0.01 && <Badge color="success" className="mt-1"><i className="fa fa-check mr-1"></i>COMPLETO</Badge>}
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
                {formasPagamento.map((fp, index) => (
                    <Card key={index} className="mb-3">
                        <CardBody>
                            <Row>
                                <Col md={5}>
                                    <FormGroup>
                                        <Label>Forma de Pagamento</Label>
                                        <Select
                                            options={listFormaPagamentoByEmpresa.map(f => ({
                                                value: f.id,
                                                label: f.name,
                                            }))}
                                            onChange={(option: any) => handleFormaPagamentoChange(index, 'formaPagamentoId', option?.value || 0)}
                                            value={
                                                fp.formaPagamentoId
                                                    ? { value: fp.formaPagamentoId, label: fp.formaPagamentoName }
                                                    : null
                                            }
                                            placeholder="Selecione..."
                                            isClearable
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={formasPagamento.length > 1 ? 6 : 7}>
                                    <FormGroup>
                                        <Label>Valor</Label>
                                        <CurrencyInput
                                            value={fp.value}
                                            onValueChange={(v) => handleFormaPagamentoChange(index, 'value', v)}
                                            name={`valor_${index}`}
                                            placeholder="R$ 0,00"
                                        />
                                    </FormGroup>
                                </Col>
                                {formasPagamento.length > 1 && (
                                    <Col md={1} className="d-flex align-items-end">
                                        <FormGroup className="w-100">
                                            <Button
                                                color="danger"
                                                size="sm"
                                                block
                                                onClick={() => handleRemoveFormaPagamento(index)}
                                            >
                                                <i className="fa fa-trash"></i>
                                            </Button>
                                        </FormGroup>
                                    </Col>
                                )}
                            </Row>
                        </CardBody>
                    </Card>
                ))}

                <Button
                    style={{ color: "#FFF", backgroundColor: "#585858" }}
                    outline
                    block
                    onClick={handleAddFormaPagamento}
                >
                    <i className="fa fa-plus mr-2"></i>
                    Adicionar Forma de Pagamento
                </Button>

                {!isFormasPagamentoValidas() && (
                    <Alert color="danger" className="mt-3">
                        <i className="fa fa-exclamation-triangle mr-2"></i>
                        A soma das formas de pagamento deve ser igual ao valor do produto.
                    </Alert>
                )}
            </>
        );
    };

    const renderStep6 = () => (
        <div>
            <h4 className="mb-2 text-center">Confirmar Dados da Venda</h4>
            
            <Card className="mb-3">
                <CardBody>
                    <h5 className="text-primary mb-3">
                        <i className="fa fa-user mr-2"></i>
                        Aluno
                    </h5>
                    <p className="mb-1"><strong>Nome:</strong> {selectedAluno?.name}</p>
                    <p className="mb-1"><strong>Curso:</strong> {selectedAluno?.cursoName || 'Não informado'}</p>
                    <p className="mb-0"><strong>Email:</strong> {selectedAluno?.email || 'Não informado'}</p>
                </CardBody>
            </Card>

            <Card className="mb-3">
                <CardBody>
                    <h5 className="text-primary mb-3">
                        <i className="fa fa-address-card mr-2"></i>
                        Cliente
                    </h5>
                    <p className="mb-0"><strong>Nome:</strong> {selectedCliente?.label || selectedCliente?.data?.name}</p>
                </CardBody>
            </Card>

            <Card className="mb-3">
                <CardBody>
                    <h5 className="text-primary mb-3">
                        <i className="fa fa-box mr-2"></i>
                        Produto{selectedProdutos.length > 1 ? 's' : ''}
                    </h5>
                    {selectedProdutos.map((produto, index) => (
                        <div key={produto.value} className={index < selectedProdutos.length - 1 ? 'mb-2 pb-2 border-bottom' : ''}>
                            <p className="mb-1"><strong>Nome:</strong> {produto.data.name}</p>
                            <p className="mb-0"><strong>Valor:</strong> R$ {produto.data.value.toFixed(2).replace('.', ',')}</p>
                        </div>
                    ))}
                    <div className="mt-2 pt-2 border-top">
                        <p className="mb-0"><strong>Valor Total:</strong> <span className="text-success">R$ {selectedProdutos.reduce((sum, p) => sum + (p.data?.value || 0), 0).toFixed(2).replace('.', ',')}</span></p>
                    </div>
                </CardBody>
            </Card>

            <Card className="mb-3">
                <CardBody>
                    <h5 className="text-primary mb-3">
                        <i className="fa fa-user-tie mr-2"></i>
                        Vendedor
                    </h5>
                    <p className="mb-0"><strong>Nome:</strong> {selectedVendedor?.data?.name}</p>
                </CardBody>
            </Card>

            <Card>
                <CardBody>
                    <h5 className="text-primary mb-3">
                        <i className="fa fa-credit-card mr-2"></i>
                        Formas de Pagamento
                    </h5>
                    {formasPagamento.map((fp, index) => (
                        <div key={index} className="mb-2">
                            <strong>{fp.formaPagamentoName || 'Não definida'}:</strong> R$ {fp.value.toFixed(2).replace('.', ',')}
                        </div>
                    ))}
                    <hr />
                    <p className="mb-0 text-right">
                        <strong>Total:</strong> R$ {getTotalAlocado().toFixed(2).replace('.', ',')}
                    </p>
                </CardBody>
            </Card>
        </div>
    );

    return (
        <Modal isOpen={isOpen} toggle={handleClose} size="xl" backdrop="static">
            <style>
                {`
                    .venda-modal-body::-webkit-scrollbar {
                        width: 10px;
                    }
                    .venda-modal-body::-webkit-scrollbar-track {
                        background: #f1f1f1;
                        border-radius: 10px;
                    }
                    .venda-modal-body::-webkit-scrollbar-thumb {
                        background: #888;
                        border-radius: 10px;
                    }
                    .venda-modal-body::-webkit-scrollbar-thumb:hover {
                        background: #555;
                    }
                    .venda-modal-body {
                        scrollbar-width: thin;
                        scrollbar-color: #888 #f1f1f1;
                    }
                `}
            </style>
            <ModalHeader toggle={handleClose}>
                Nova Venda
            </ModalHeader>
            <ModalBody 
                className="venda-modal-body"
                style={{ 
                    maxHeight: 'calc(100vh - 250px)', 
                    overflowY: 'auto',
                    overflowX: 'hidden'
                }}
            >
                {renderStepIndicator()}
                
                <div style={{ minHeight: '300px' }}>
                    {currentStep === 1 && renderStep1()}
                    {currentStep === 2 && renderStep2()}
                    {currentStep === 3 && renderStep3()}
                    {currentStep === 4 && renderStep4()}
                    {currentStep === 5 && renderStep5()}
                    {currentStep === 6 && renderStep6()}
                </div>
            </ModalBody>
            <ModalFooter>
                {currentStep > 1 && (
                    <Button
                        style={{ color: "#FFF", backgroundColor: "#d9534f" }}
                        onClick={handlePreviousStep}
                        disabled={isSubmitting}
                    >
                        <i className="fa fa-arrow-left mr-2"></i>
                        Voltar
                    </Button>
                )}
                {currentStep < 6 ? (
                    <Button
                        style={{ color: "#FFF", backgroundColor: "#585858" }}
                        onClick={handleNextStep}
                    >
                        Próximo
                        <i className="fa fa-arrow-right ml-2"></i>
                    </Button>
                ) : (
                    <Button
                        style={{ color: "#FFF", backgroundColor: "#5cb85c" }}
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <i className="fa fa-spinner fa-spin mr-2"></i>
                                Criando...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-check mr-2"></i>
                                Confirmar Venda
                            </>
                        )}
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default CreateVendaModal;
